/**
 * Presentational Stripe Checkout provider.
 * Does NOT create sessions or call useClientStripeSessionByToken.
 * Receives clientSecretPromise and defaultValues from caller; only wires Stripe's CheckoutProvider.
 */
import { CheckoutProvider } from '@stripe/react-stripe-js/checkout';
import { loadStripe } from '@stripe/stripe-js';
import React from 'react';
import { StripeCheckoutForm } from './StripeCheckoutForm';
import type { StripeCheckoutProviderProps } from './types';

type CheckoutProviderOptions = React.ComponentProps<typeof CheckoutProvider>['options'];

export const CheckoutFormProviderWrap = ({
	stripePublicId,
	clientSecretPromise,
	defaultValues,
	prefilledEmail,
	onSuccessClose,
	submitButtonLabel,
	loggingName,
	isProcessingPayment,
}: StripeCheckoutProviderProps) => {
	const stripePromise = React.useMemo(() => {
		if (stripePublicId) {
			return loadStripe(stripePublicId);
		}
		return null;
	}, [stripePublicId]);

	const options: CheckoutProviderOptions = {
		clientSecret: clientSecretPromise,
		...(defaultValues && { defaultValues: defaultValues as CheckoutProviderOptions['defaultValues'] }),
	};

	if (!stripePromise) {
		return null;
	}

	return (
		<CheckoutProvider stripe={stripePromise} options={options}>
			<StripeCheckoutForm
				prefilledEmail={prefilledEmail}
				onSuccessClose={onSuccessClose}
				submitButtonLabel={submitButtonLabel}
				loggingName={loggingName}
				isProcessingPayment={isProcessingPayment}
			/>
		</CheckoutProvider>
	);
};
